'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { cva } from 'class-variance-authority'
import { hashHighlightString } from './hashHighlightString'

export const highlightVariants = {
  info: 'text-info-fg',
  success: 'text-success-fg',
  warning: 'text-warning-fg',
  error: 'text-error-fg',
  primary: 'text-primary',
}

export type HighlightVariantsEnum = keyof typeof highlightVariants

const highlightedTextCva = cva('inline font-bold underline', {
  variants: {
    highlightVariants,
  },
})

export type HighlightedTextProps = UiComponentProps<{
  // Space-separated search terms
  highlight: string

  // Matching respects case
  caseSensitive: boolean

  // Matching respects accents
  accentSensitive: boolean

  // Full text to render
  children: string

  // Color of the highlighted parts
  color: HighlightVariantsEnum
}>

export function HighlightedText({
  highlight,
  caseSensitive,
  accentSensitive,
  color,
  children,
  className,
  id,
  'data-testid': dataTestId = 'highlighted-text',
}: HighlightedTextProps): React.ReactNode {
  const searchWords = highlight.split(' ')
  const hashedText = hashHighlightString({
    text: children,
    search: searchWords,
    options: {
      caseSensitive,
      accentSensitive,
    },
  })

  return (
    <p id={id} data-testid={dataTestId} className={twMerge('whitespace-pre-wrap', className)}>
      {hashedText.map(({ text, isHighlight }, index) => {
        const key = `${text}-${index}`

        return (
          <span
            key={key}
            data-testid={isHighlight ? `${dataTestId}/highlight` : undefined}
            className={isHighlight ? highlightedTextCva({ highlightVariants: color }) : undefined}
          >
            {text}
          </span>
        )
      })}
    </p>
  )
}
